import { useQuery } from '@apollo/client/react';
import { useSetAtom } from 'jotai';
import { useCallback, useRef, useState } from 'react';

import { GuestPatientForm } from '~/components/partials/GuestPatientForm';
import { Fields } from '~/components/partials/GuestPatientForm/types';
import { GetDirectVisitorsDocument } from '~/graphql/generated/graphql';
import { useScrollToTop } from '~/hooks/use-scroll-to-top';
import { newPatientDrawerState } from '~/state/patient/atoms';
import { NewPatientPanelMap } from '~/state/patient/types';

export const DEFAULT_PER_PAGE = 3;

export const useSubmitNewPatient = () => {
  const formRef = useRef<RefAttributeType<typeof GuestPatientForm>>(null);
  const [containerRef, scrollToTop] = useScrollToTop<HTMLDivElement>();

  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const setState = useSetAtom(newPatientDrawerState);

  // FIXME: LazyQueryではasync・awaitが使えないため、通常のQueryのrefetch利用
  // apolloのバージョンアップで使えるようになったら、use-fetch-direct_visitors.tsへ置き換え
  const { refetch } = useQuery(GetDirectVisitorsDocument, {
    skip: !isSubmitting,
    variables: {
      phoneNumber: '',
      page: 1,
      perPage: DEFAULT_PER_PAGE,
    },
  });

  const handleSubmit = useCallback(
    async (values: Fields) => {
      setIsSubmitting(true);
      setError(null);

      try {
        const directVisitorList = await refetch({
          phoneNumber: values.mobilePhone,
        });

        const listData = directVisitorList.data;
        const totalDirectVisitorsWithSamePhoneNumber =
          listData?.me?.__typename === 'Practitioner'
            ? (listData.me.organization.directVisitors.nodesCount ?? 0)
            : 0;

        setState((_state) => ({
          ..._state,
          currentPanel: NewPatientPanelMap.confirm,
          inputValue: values,
          perPage: DEFAULT_PER_PAGE,
          existingPhoneNumber: totalDirectVisitorsWithSamePhoneNumber > 0,
        }));

        formRef.current?.resetForm();
        scrollToTop();
      } catch (error) {
        setError(error?.message || 'エラーが発生しました');
        scrollToTop();
      }
      setIsSubmitting(false);
    },
    [refetch, scrollToTop, setState],
  );

  return {
    isSubmitting,
    error,
    containerRef,
    formRef,
    handleSubmit,
  };
};
